import api from './api'

export type BankReconAccount = {
  key: string
  label: string
  qbo_account_id: string
  account_type: string
  parser: string
  qbo_display_name?: string | null
}

export type MonthAccountStatus = {
  account_key: string
  status: string
  source_filename?: string | null
}

export type MonthSummaryItem = {
  report_month: string
  accounts: MonthAccountStatus[]
  bank_reconciled_checked?: boolean
}

export type StatementLine = {
  line_key: string
  posted_date: string
  description: string
  amount_signed: string
  page: number
  seq: number
}

export type QboTxn = {
  txn_id: string
  qbo_line_key: string
  txn_type: string
  txn_date: string
  amount_signed: string
  doc_number: string
  name: string
  memo: string
  cleared_status?: string | null
  url: string
}

export type MatchPair = {
  line_key?: string | null
  txn_id?: string | null
  qbo_line_key?: string | null
  line?: StatementLine | null
  txn?: QboTxn | null
}

export type OverrideBucketItem = {
  line_key?: string | null
  txn_id?: string | null
  qbo_line_key?: string | null
  kind: string
  note: string
  override_id?: number | null
  link_group_id?: string | null
  line?: StatementLine | null
  txn?: QboTxn | null
}

export type MatchBuckets = {
  matched_cleared: MatchPair[]
  matched_uncleared: MatchPair[]
  statement_only: StatementLine[]
  qbo_only_in_window: QboTxn[]
  qbo_outstanding: QboTxn[]
  qbo_stale: QboTxn[]
  qbo_unreconciled_out_of_window: QboTxn[]
  overridden: OverrideBucketItem[]
}

export type HeadlineNumbers = {
  statement_ending_balance?: string | null
  qbo_cleared_balance?: string | null
  expected_difference?: string | null
  beginning_balance_drift?: string | null
  exception_count: number
}

export type StatementHeader = {
  source_filename?: string | null
  statement_start?: string | null
  statement_end?: string | null
  beginning_balance?: string | null
  ending_balance?: string | null
  line_count: number
  warnings: string[]
  uploaded_at?: string | null
}

export type MonthDetailResponse = {
  account_key: string
  report_month: string
  statement?: StatementHeader | null
  headlines: HeadlineNumbers
  buckets: MatchBuckets
  qbo_fetched_at?: string | null
  qbo_status_available: boolean
  content_hash?: string | null
  reconciled_at?: string | null
  reconciled_by?: string | null
  reconcile_note?: string | null
}

export type UploadResponse = {
  account_key: string
  report_month: string
  detail: MonthDetailResponse
}

export type OverrideKind = 'match' | 'ignore_statement_line' | 'ignore_qbo_txn'

export type AddOverrideRequest = {
  kind: OverrideKind
  statement_line_key?: string
  statement_line_keys?: string[]
  qbo_txn_id?: string
  qbo_txn_ids?: string[]
  note: string
}

export type MarkReconciledRequest = {
  reconciled: boolean
  note?: string
}

export type MarkReconciledResponse = {
  account_key: string
  report_month: string
  reconciled: boolean
  detail: MonthDetailResponse
}

const multipartHeaders = { headers: { 'Content-Type': 'multipart/form-data' } }

export async function listAccounts(): Promise<{ accounts: BankReconAccount[] }> {
  const { data } = await api.get<{ accounts: BankReconAccount[] }>('/bank-recon/accounts')
  return data
}

export async function listMonths(end?: string): Promise<{ months: MonthSummaryItem[] }> {
  const { data } = await api.get<{ months: MonthSummaryItem[] }>('/bank-recon/months', {
    params: end ? { end } : undefined,
  })
  return data
}

export async function getMonthDetail(
  accountKey: string,
  reportMonth: string,
): Promise<MonthDetailResponse> {
  const { data } = await api.get<MonthDetailResponse>(
    `/bank-recon/${accountKey}/${reportMonth}`,
  )
  return data
}

export async function uploadStatement(
  accountKey: string,
  reportMonth: string,
  file: File,
): Promise<UploadResponse> {
  const form = new FormData()
  form.append('file', file)
  const { data } = await api.post<UploadResponse>(
    `/bank-recon/${accountKey}/${reportMonth}/upload`,
    form,
    multipartHeaders,
  )
  return data
}

export async function refreshMonth(
  accountKey: string,
  reportMonth: string,
): Promise<MonthDetailResponse> {
  const { data } = await api.post<MonthDetailResponse>(
    `/bank-recon/${accountKey}/${reportMonth}/refresh`,
  )
  return data
}

export async function addOverride(
  accountKey: string,
  reportMonth: string,
  body: AddOverrideRequest,
): Promise<{ detail: MonthDetailResponse }> {
  const { data } = await api.post<{ detail: MonthDetailResponse }>(
    `/bank-recon/${accountKey}/${reportMonth}/overrides`,
    body,
  )
  return data
}

export async function removeOverride(
  accountKey: string,
  reportMonth: string,
  overrideId: number,
): Promise<{ detail: MonthDetailResponse }> {
  const { data } = await api.delete<{ detail: MonthDetailResponse }>(
    `/bank-recon/${accountKey}/${reportMonth}/overrides/${overrideId}`,
  )
  return data
}

export async function removeOverrides(
  accountKey: string,
  reportMonth: string,
  overrideIds: number[],
): Promise<{ detail: MonthDetailResponse }> {
  const { data } = await api.delete<{ detail: MonthDetailResponse }>(
    `/bank-recon/${accountKey}/${reportMonth}/overrides/batch`,
    {
      params: { override_ids: overrideIds },
      paramsSerializer: {
        indexes: null,
      },
    },
  )
  return data
}

export async function markReconciled(
  accountKey: string,
  reportMonth: string,
  body: MarkReconciledRequest,
): Promise<MarkReconciledResponse> {
  const { data } = await api.post<MarkReconciledResponse>(
    `/bank-recon/${accountKey}/${reportMonth}/reconciled`,
    body,
  )
  return data
}

export async function downloadStatementFile(
  accountKey: string,
  reportMonth: string,
): Promise<void> {
  const response = await api.get<Blob>(`/bank-recon/${accountKey}/${reportMonth}/file`, {
    responseType: 'blob',
  })
  const disposition = response.headers['content-disposition'] as string | undefined
  let filename = `${reportMonth.replaceAll('-', '')}_${accountKey}.pdf`
  if (disposition) {
    const match = /filename="?([^";\n]+)"?/.exec(disposition)
    if (match?.[1]) filename = match[1]
  }
  const url = URL.createObjectURL(response.data)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export async function deleteMonth(
  accountKey: string,
  reportMonth: string,
  confirm: boolean,
): Promise<void> {
  await api.delete(`/bank-recon/${accountKey}/${reportMonth}`, {
    params: confirm ? { confirm: true } : undefined,
  })
}
